import { subYears, subMonths, subWeeks, subDays, differenceInYears, differenceInMonths, differenceInWeeks, differenceInDays } from "date-fns";

export const formatDateOnly = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

export const formatSlotTimeDisplay = (time) => {
    if (!time) return "";
    const [hourStr, minuteStr] = time.split(":");
    const hour24 = parseInt(hourStr, 10);
    if (Number.isNaN(hour24)) return time;
    const period = hour24 >= 12 ? "PM" : "AM";
    const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
    return `${hour12}:${minuteStr} ${period}`;
};

export const formatDateDDMMYYYY = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
};

const parseFlexibleDate = (date) => {
    if (!date) return null;
    const ddmmyyyy = typeof date === "string" && date.match(/^(\d{2})[-/](\d{2})[-/](\d{4})\s+(\d{2}):(\d{2}):(\d{2})$/);
    if (ddmmyyyy) {
        const [, day, month, year, hours, minutes, seconds] = ddmmyyyy;
        return new Date(year, month - 1, day, hours, minutes, seconds);
    }
    return new Date(date);
};

export const formatDateTimeAMPM = (date) => {
    if (!date) return "";
    const d = parseFlexibleDate(date);
    if (!d || Number.isNaN(d.getTime())) return date;
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    const minutes = String(d.getMinutes()).padStart(2, "0");
    const period = d.getHours() >= 12 ? "PM" : "AM";
    const hours12 = d.getHours() % 12 === 0 ? 12 : d.getHours() % 12;
    return `${day}-${month}-${year} ${hours12}:${minutes} ${period}`;
};

export const formatTimeAMPM = (date) => {
    if (!date) return "";
    const d = parseFlexibleDate(date);
    if (!d || Number.isNaN(d.getTime())) return date;
    const minutes = String(d.getMinutes()).padStart(2, "0");
    const period = d.getHours() >= 12 ? "PM" : "AM";
    const hours12 = d.getHours() % 12 === 0 ? 12 : d.getHours() % 12;
    return `${hours12}:${minutes} ${period}`;
};

export const formatDateTimeSlashed = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    const hours = String(d.getHours()).padStart(2, "0");
    const minutes = String(d.getMinutes()).padStart(2, "0");
    const seconds = String(d.getSeconds()).padStart(2, "0");
    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
};

export const calculateDOBFromAge = (value, unit) => {
    if (!value) return null;
    const today = new Date();
    let newDOB;

    if (unit === "Year") {
        let [years, months] = value.split(".");
        years = parseInt(years) || 0;
        months = parseInt(months) || 0;
        newDOB = subYears(today, years);
        if (months > 0) newDOB = subMonths(newDOB, months);
    } else if (unit === "Month") {
        newDOB = subMonths(today, parseInt(value));
    } else if (unit === "Week") {
        newDOB = subWeeks(today, parseInt(value));
    } else if (unit === "Day") {
        newDOB = subDays(today, parseInt(value));
    }

    return newDOB;
};

export const formatAgeDisplay = (age) => {
    if (age === undefined || age === null || age === "") return "";
    const str = String(age);
    if (str.includes(".")) {
        const months = parseInt(str.split(".")[1], 10) || 0;
        return `${months} month${months === 1 ? "" : "s"}`;
    }
    if (str === "0") return "Less than 1 month";
    const years = parseInt(str, 10) || 0;
    return `${years} year${years === 1 ? "" : "s"}`;
};

export const calculateAgeFromDOB = (date) => {
    if (!date) return "";
    const today = new Date();
    const dob = new Date(date);
    let months = (today.getFullYear() - dob.getFullYear()) * 12 + (today.getMonth() - dob.getMonth());
    if (today.getDate() < dob.getDate()) months--;
    if (months <= 0) return "0";
    const years = Math.floor(months / 12);
    if (years > 0) return `${years}`;
    return `0.${String(months).padStart(2, "0")}`;
};

export const calculateAgeUnitFromDOB = (date) => {
    if (!date) return { value: "", unit: "Year" };
    const today = new Date();
    const birth = new Date(date);

    const years = differenceInYears(today, birth);
    if (years > 0) {
        return { value: String(years), unit: "Year" };
    }

    const months = differenceInMonths(today, birth);
    if (months > 0) return { value: String(months), unit: "Month" };

    const weeks = differenceInWeeks(today, birth);
    if (weeks > 0) return { value: String(weeks), unit: "Week" };

    return { value: String(differenceInDays(today, birth)), unit: "Day" };
};

export const HVT_VISIT_STATUS = [
    "Registered",
    "Phlebo Reached",
    "Samples Collected",
    "Samples Delivered",
    "Cancelled",
    "Aborted",
    "Wait List",
];

export const toPhleboGender = (gender) => (gender === "Male" || gender === "Female") ? gender : "";

export const SLOT_PERIODS = ["Morning", "Afternoon", "Evening"];

export const groupSlotsByPeriod = (slots) => {
    const groups = { Morning: [], Afternoon: [], Evening: [] };
    (slots || []).forEach((slot) => {
        const hour = parseInt((slot.Slot_TIME || "").split(":")[0], 10);
        if (Number.isNaN(hour)) return;
        if (hour < 12) groups.Morning.push(slot);
        else if (hour < 17) groups.Afternoon.push(slot);
        else groups.Evening.push(slot);
    });
    return groups;
};
